import { isRialString } from '@clinic/money-date';
import { z } from 'zod';
import { CLINIC_SECTIONS } from './access.js';
import {
  AMOUNT_MAX_RIAL,
  AMOUNT_TOO_BIG,
  AmountRialSchema,
  DateOnlySchema,
  IsoDateTimeSchema,
  PAGE_LIMIT_MAX,
  RialStringSchema,
  UuidSchema,
  optionalText,
} from './common.js';

/**
 * The clinic's books (docs/clinic-finance/design.md, ADR-031): a one-sided, append-only journal.
 * Amounts are rial strings; balances, revenue and profit are sums of effect columns that the
 * database computes on insert, never numbers kept anywhere else (BR-12, BR-14).
 */

export const JOURNAL_TYPES = [
  'charge',
  'payment',
  'discount',
  'patient_refund',
  'expense',
  'material_purchase',
  'adjustment',
  'opening_balance',
  'reversal',
  // What the clinic owes its suppliers (ADR-038)
  'vendor_payment',
  'vendor_opening_balance',
  // What the clinic is owed by payment providers (ADR-058)
  'provider_payment',
  'provider_fee',
  'provider_settlement',
] as const;
export type JournalType = (typeof JOURNAL_TYPES)[number];

/** What each entry traces back to; a manual entry points to itself. */
export const JOURNAL_SOURCE_TYPES = [
  'visit_item',
  'patient_package',
  'stock_receipt',
  'journal_entry',
  'manual',
  // an opening balance brought in by a patient import (ADR-032)
  'patient_import',
  // the material purchase of a purchase price given after the receipt (ADR-053)
  'receipt_cost',
  // Provider receipts and provider settlements (ADR-058)
  'provider_receipt',
  'provider_settlement',
] as const;
export type JournalSourceType = (typeof JOURNAL_SOURCE_TYPES)[number];

export const PAYMENT_METHODS = ['cash', 'pos', 'card_to_card', 'bank_transfer'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** Suggestions only; the category is free text (like the inventory categories). */
export const SUGGESTED_EXPENSE_CATEGORIES = [
  'اجاره',
  'حقوق و دستمزد',
  'قبوض',
  'تبلیغات',
  'تجهیزات',
  'تعمیرات',
  'مالیات و عوارض',
  'سایر',
] as const;

/** A money command's amount: above zero, up to the ceiling (AMOUNT_MAX_RIAL). */
export const PositiveRialSchema = z.string().superRefine((v, ctx) => {
  if (!isRialString(v) || BigInt(v) <= 0n) {
    ctx.addIssue({ code: 'custom', message: 'مبلغ باید بیشتر از صفر باشد.' });
  } else if (BigInt(v) > AMOUNT_MAX_RIAL) {
    ctx.addIssue({ code: 'custom', message: AMOUNT_TOO_BIG });
  }
});
/** An adjustment: signed, not zero, up to the ceiling either way. */
export const NonZeroRialSchema = z.string().superRefine((v, ctx) => {
  if (!isRialString(v)) {
    ctx.addIssue({ code: 'custom', message: 'مبلغ باید عدد صحیح ریالی به‌صورت متن باشد.' });
  } else if (BigInt(v) === 0n) {
    ctx.addIssue({ code: 'custom', message: 'مبلغ نمی‌تواند صفر باشد.' });
  } else if (BigInt(v) > AMOUNT_MAX_RIAL || BigInt(v) < -AMOUNT_MAX_RIAL) {
    ctx.addIssue({ code: 'custom', message: AMOUNT_TOO_BIG });
  }
});

export const ReasonSchema = z.string().trim().min(3, { message: 'دلیل را بنویسید.' }).max(300);

/**
 * The section a manual command goes to (ADR-036): the patient has one balance per section. Without
 * it, the clinic part; the server and RLS check the caller reaches it.
 */
export const CommandSectionSchema = z.enum(CLINIC_SECTIONS).default('general');
export const SectionFilterSchema = z.enum(CLINIC_SECTIONS).optional();

export const ProviderRefInputSchema = z.object({
  providerId: UuidSchema,
  termsRevision: z.number().int().min(1),
  externalRef: z
    .string()
    .trim()
    .max(64)
    .nullish()
    .transform((v) => (v ? v : null)),
});
export type ProviderRefInput = z.input<typeof ProviderRefInputSchema>;

// ---- entries ----

export const JournalEntrySchema = z.object({
  id: UuidSchema,
  type: z.enum(JOURNAL_TYPES),
  /** Copied from the visit, package or reversed entry; given only for a manual entry (ADR-036). */
  section: z.enum(CLINIC_SECTIONS),
  amountRial: RialStringSchema,
  occurredOn: DateOnlySchema,
  patientId: UuidSchema.nullable(),
  patientName: z.string().nullable(),
  visitId: UuidSchema.nullable(),
  serviceId: UuidSchema.nullable(),
  serviceName: z.string().nullable(),
  practitionerMembershipId: UuidSchema.nullable(),
  patientPackageId: UuidSchema.nullable(),
  /** The supplier of a purchase on credit or of a payment to it (ADR-038). */
  vendorId: UuidSchema.nullable(),
  vendorName: z.string().nullable(),
  /** The payment provider for provider receipts and settlements (ADR-058). */
  providerId: UuidSchema.nullable(),
  providerName: z.string().nullable(),
  providerReceiptId: UuidSchema.nullable(),
  providerSettlementId: UuidSchema.nullable(),
  method: z.enum(PAYMENT_METHODS).nullable(),
  category: z.string().nullable(),
  note: z.string().nullable(),
  reason: z.string().nullable(),
  sourceType: z.enum(JOURNAL_SOURCE_TYPES),
  sourceId: UuidSchema,
  reversesId: UuidSchema.nullable(),
  /** The reversal of this entry, if any (an entry is reversed at most once). */
  reversedById: UuidSchema.nullable(),
  /** Effects (ADR-031), signed: on the patient's balance, revenue, costs and cash. */
  patientEffectRial: RialStringSchema,
  revenueEffectRial: RialStringSchema,
  costEffectRial: RialStringSchema,
  cashEffectRial: RialStringSchema,
  /** On what the clinic owes the vendor (ADR-038). */
  payableEffectRial: RialStringSchema,
  /** On what payment providers owe the clinic (ADR-058). */
  receivableEffectRial: RialStringSchema,
  createdAt: IsoDateTimeSchema,
});
export type JournalEntry = z.infer<typeof JournalEntrySchema>;

export const JournalListQuerySchema = z.object({
  from: DateOnlySchema.optional(),
  to: DateOnlySchema.optional(),
  type: z.enum(JOURNAL_TYPES).optional(),
  section: SectionFilterSchema,
  cursor: UuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(50),
});
/** The quick CSV of the journal (docs/parity-gaps/design.md §2): a bounded date range, never open-ended. */
export const FINANCE_EXPORT_MAX_DAYS = 366;
export const FINANCE_EXPORT_MAX_ROWS = 20_000;
export const FinanceExportQuerySchema = z
  .object({ from: DateOnlySchema, to: DateOnlySchema })
  .refine((q) => q.from <= q.to, { message: 'بازه‌ی تاریخ معتبر نیست.', path: ['to'] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= (FINANCE_EXPORT_MAX_DAYS - 1) * 86_400_000, {
    message: 'بازه حداکثر ۳۶۶ روز است.',
    path: ['to'],
  });

export const JournalListResponseSchema = z.object({
  items: z.array(JournalEntrySchema),
  nextCursor: UuidSchema.nullable(),
});
export type JournalListResponse = z.infer<typeof JournalListResponseSchema>;

// ---- a patient's account ----

/** What the patient owes (positive) or is owed (negative), and what the next commands may take. */
export const PatientBalanceSchema = z.object({
  patientId: UuidSchema,
  /** Each section has its own balance (ADR-036, BR-57). */
  section: z.enum(CLINIC_SECTIONS),
  balanceRial: RialStringSchema,
  /** Most a payment or discount may be now (BR-15). */
  payableRial: RialStringSchema,
  /** Most a refund may be now: the patient's credit. */
  refundableRial: RialStringSchema,
  /** The section has a journal entry for the patient; a zero balance without one is not «تسویه». */
  hasEntries: z.boolean(),
});
export type PatientBalance = z.infer<typeof PatientBalanceSchema>;

export const PATIENT_PACKAGE_STATES = ['active', 'used_up', 'expired', 'cancelled'] as const;
export type PatientPackageState = (typeof PATIENT_PACKAGE_STATES)[number];

/** How a package is paid (ADR-037): owed and paid any time, in full at the sale, or in installments. */
export const PACKAGE_PAYMENT_TERMS = ['any', 'upfront', 'installments'] as const;
export type PackagePaymentTerms = (typeof PACKAGE_PAYMENT_TERMS)[number];
/** What a cancellation returns: the value of the paid sessions left (less a penalty), or nothing. */
export const CANCEL_POLICIES = ['pro_rata', 'none'] as const;
export type CancelPolicy = (typeof CANCEL_POLICIES)[number];

export const PatientPackageSchema = z.object({
  id: UuidSchema,
  patientId: UuidSchema,
  servicePackageId: UuidSchema,
  /** The first service (a package may hold several of one section). */
  serviceId: UuidSchema,
  serviceName: z.string(),
  section: z.enum(CLINIC_SECTIONS),
  name: z.string(),
  /** Totals over the services, gift sessions included. */
  sessions: z.number().int(),
  usedSessions: z.number().int(),
  remainingSessions: z.number().int(),
  items: z.array(
    z.object({
      serviceId: UuidSchema,
      serviceName: z.string(),
      sessions: z.number().int(),
      giftSessions: z.number().int(),
      usedSessions: z.number().int(),
      remainingSessions: z.number().int(),
    }),
  ),
  priceRial: RialStringSchema,
  expiresOn: DateOnlySchema.nullable(),
  state: z.enum(PATIENT_PACKAGE_STATES),
  soldAt: IsoDateTimeSchema,
  cancelledAt: IsoDateTimeSchema.nullable(),
  cancelPolicy: z.enum(CANCEL_POLICIES),
  cancelPenaltyBp: z.number().int(),
  maxExtensionDays: z.number().int(),
  extendedDays: z.number().int(),
  installments: z.array(z.object({ seq: z.number().int(), dueOn: DateOnlySchema, amountRial: RialStringSchema })),
});
export type PatientPackage = z.infer<typeof PatientPackageSchema>;

/**
 * What a patient list shows about money: each section's balance that is not zero (in the caller's
 * till sections) and the active packages with sessions left.
 */
export const PatientDigestSchema = z.object({
  patientId: UuidSchema,
  balances: z.array(z.object({ section: z.enum(CLINIC_SECTIONS), balanceRial: RialStringSchema })),
  packages: z.array(
    z.object({
      id: UuidSchema,
      name: z.string(),
      section: z.enum(CLINIC_SECTIONS),
      remainingSessions: z.number().int(),
      expiresOn: DateOnlySchema.nullable(),
    }),
  ),
});
export type PatientDigest = z.infer<typeof PatientDigestSchema>;
export const PatientDigestsResponseSchema = z.object({ items: z.array(PatientDigestSchema) });
export type PatientDigestsResponse = z.infer<typeof PatientDigestsResponseSchema>;

export const PatientAccountSchema = z.object({
  /** One per section the caller's till reaches, in section order (ADR-036). */
  balances: z.array(PatientBalanceSchema),
  entries: z.array(JournalEntrySchema),
  nextCursor: UuidSchema.nullable(),
  packages: z.array(PatientPackageSchema),
});
export type PatientAccount = z.infer<typeof PatientAccountSchema>;
export const PatientAccountQuerySchema = z.object({
  /** Narrows the statement and the packages to one section; the balances stay all. */
  section: SectionFilterSchema,
  cursor: UuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX).default(30),
});

/** Every patient money command answers with the new entry and the new balance of its section. */
export const PatientCommandResultSchema = z.object({
  entry: JournalEntrySchema,
  balance: PatientBalanceSchema,
});
export type PatientCommandResult = z.infer<typeof PatientCommandResultSchema>;

export const PaymentInputSchema = z.object({
  section: CommandSectionSchema,
  amountRial: PositiveRialSchema,
  method: z.enum(PAYMENT_METHODS),
  note: optionalText(300),
  /** The visit this payment was taken for, if any (a reference for the statement, not a limit). */
  visitId: UuidSchema.nullish().transform((v) => v ?? null),
});
export type PaymentInput = z.input<typeof PaymentInputSchema>;

export const DiscountInputSchema = z.object({
  section: CommandSectionSchema,
  amountRial: PositiveRialSchema,
  reason: ReasonSchema,
  visitId: UuidSchema.nullish().transform((v) => v ?? null),
});
export type DiscountInput = z.input<typeof DiscountInputSchema>;

export const RefundInputSchema = z.object({
  section: CommandSectionSchema,
  amountRial: PositiveRialSchema,
  method: z.enum(PAYMENT_METHODS),
  reason: ReasonSchema,
  provider: z
    .undefined({ message: 'بازپرداخت، هزینه و پرداخت به تأمین‌کننده از طریق سرویس پرداخت ثبت نمی‌شوند.' })
    .optional(),
});
export type RefundInput = z.input<typeof RefundInputSchema>;

export const AdjustmentInputSchema = z.object({
  section: CommandSectionSchema,
  amountRial: NonZeroRialSchema,
  reason: ReasonSchema,
});
export type AdjustmentInput = z.input<typeof AdjustmentInputSchema>;

export const OpeningBalanceInputSchema = z.object({
  section: CommandSectionSchema,
  amountRial: PositiveRialSchema,
  note: optionalText(300),
});
export type OpeningBalanceInput = z.input<typeof OpeningBalanceInputSchema>;

export const ReverseInputSchema = z.object({ reason: ReasonSchema });
export type ReverseInput = z.input<typeof ReverseInputSchema>;

export const ReverseResultSchema = z.object({
  entry: JournalEntrySchema,
  balance: PatientBalanceSchema.nullable(),
});

// ---- clinic costs ----

// ---- suppliers and what the clinic owes them (ADR-038) ----

export const VENDOR_INSTALLMENTS_MAX = 24;
export const VendorInstallmentsInputSchema = z
  .array(z.object({ dueOn: DateOnlySchema, amountRial: PositiveRialSchema }))
  .max(VENDOR_INSTALLMENTS_MAX)
  .default([]);
/** A purchase on credit: the vendor and, optionally, when it falls due (the sum = the amount). */
export const CreditInputSchema = z.object({ vendorId: UuidSchema, installments: VendorInstallmentsInputSchema });
export type CreditInput = z.input<typeof CreditInputSchema>;

export const VENDOR_STATUSES = ['active', 'archived'] as const;
export const VendorSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  phone: z.string().nullable(),
  note: z.string().nullable(),
  status: z.enum(VENDOR_STATUSES),
  /** What the clinic owes it now (positive), derived from the books. */
  balanceRial: RialStringSchema,
  /** The part not covered by installments still to come: due now. */
  overdueRial: RialStringSchema,
  nextDueOn: DateOnlySchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Vendor = z.infer<typeof VendorSchema>;
export const VendorListResponseSchema = z.object({
  items: z.array(VendorSchema),
  totalRial: RialStringSchema,
  overdueRial: RialStringSchema,
});
export type VendorListResponse = z.infer<typeof VendorListResponseSchema>;
export const VendorListQuerySchema = z.object({
  includeArchived: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});
export const VendorInputSchema = z.object({
  name: z.string().trim().min(1, { message: 'نام تأمین‌کننده را بنویسید.' }).max(120),
  phone: z
    .string()
    .trim()
    .max(30)
    .refine((v) => v === '' || v.length >= 3, { message: 'شماره‌ی تلفن کوتاه است.' })
    .nullish()
    .transform((v) => (v ? v : null)),
  note: optionalText(500),
});
export type VendorInput = z.input<typeof VendorInputSchema>;
export const VendorStatementSchema = z.object({
  vendor: VendorSchema,
  entries: z.array(JournalEntrySchema),
  installments: z.array(
    z.object({ entryId: UuidSchema, seq: z.number().int(), dueOn: DateOnlySchema, amountRial: RialStringSchema }),
  ),
});
export type VendorStatement = z.infer<typeof VendorStatementSchema>;
export const VendorPaymentInputSchema = z.object({
  amountRial: PositiveRialSchema,
  method: z.enum(PAYMENT_METHODS),
  note: optionalText(300),
  provider: z
    .undefined({ message: 'بازپرداخت، هزینه و پرداخت به تأمین‌کننده از طریق سرویس پرداخت ثبت نمی‌شوند.' })
    .optional(),
});
export type VendorPaymentInput = z.input<typeof VendorPaymentInputSchema>;
export const VendorOpeningInputSchema = z.object({
  amountRial: PositiveRialSchema,
  note: optionalText(300),
  installments: VendorInstallmentsInputSchema,
});
export type VendorOpeningInput = z.input<typeof VendorOpeningInputSchema>;
export const VendorCommandResultSchema = z.object({ entry: JournalEntrySchema, vendor: VendorSchema });
export type VendorCommandResult = z.infer<typeof VendorCommandResultSchema>;

export const ExpenseInputSchema = z.object({
  /** A cost of the laser section, e.g. its device service (ADR-036); the clinic part otherwise. */
  section: CommandSectionSchema,
  amountRial: PositiveRialSchema,
  category: z.string().trim().min(1, { message: 'دسته‌ی هزینه را بنویسید.' }).max(40),
  /** The day it was spent (DatePicker); not in the future (the service checks) and not before 2000. */
  occurredOn: DateOnlySchema.refine((v) => v >= '2000-01-01', { message: 'تاریخ هزینه معتبر نیست.' }),
  method: z
    .enum(PAYMENT_METHODS)
    .nullish()
    .transform((v) => v ?? null),
  note: optionalText(300),
  /** Bought on credit from a supplier (ADR-038): a cost now, paid later; then no method. */
  credit: CreditInputSchema.nullish().transform((v) => v ?? null),
  provider: z
    .undefined({ message: 'بازپرداخت، هزینه و پرداخت به تأمین‌کننده از طریق سرویس پرداخت ثبت نمی‌شوند.' })
    .optional(),
}).refine((v) => v.credit === null || v.method === null, {
  message: 'هزینه‌ی نسیه روش پرداخت ندارد.',
  path: ['method'],
});
export type ExpenseInput = z.input<typeof ExpenseInputSchema>;

// ---- receivables and a day's takings ----

export const ReceivablesResponseSchema = z.object({
  items: z.array(
    z.object({
      patientId: UuidSchema,
      patientName: z.string(),
      phone: z.string().nullable(),
      /** A patient who owes in two sections is listed once per section. */
      section: z.enum(CLINIC_SECTIONS),
      balanceRial: RialStringSchema,
      lastEntryOn: DateOnlySchema,
      /** The part not covered by installments still to come (ADR-037): due now. */
      overdueRial: RialStringSchema,
      /** The next installment of an active package, if any. */
      nextDueOn: DateOnlySchema.nullable(),
    }),
  ),
  count: z.number().int(),
  totalRial: RialStringSchema,
});
export type ReceivablesResponse = z.infer<typeof ReceivablesResponseSchema>;

/**
 * The home page's money to follow up (docs/admin-today), summed by the server for the sections of
 * the reader's till: what patients owe, what is past due of an installment plan, and the packages
 * about to expire (within `days`) with sessions left, the first few named.
 */
export const FinanceAttentionSchema = z.object({
  receivables: z.object({ count: z.number().int(), totalRial: RialStringSchema }),
  overdue: z.object({ count: z.number().int(), totalRial: RialStringSchema }),
  expiringPackages: z.object({
    count: z.number().int(),
    days: z.number().int(),
    items: z.array(
      z.object({
        id: UuidSchema,
        patientId: UuidSchema,
        patientName: z.string(),
        section: z.enum(CLINIC_SECTIONS),
        name: z.string(),
        expiresOn: DateOnlySchema,
        remainingSessions: z.number().int(),
      }),
    ),
  }),
});
export type FinanceAttention = z.infer<typeof FinanceAttentionSchema>;

export const DayQuerySchema = z.object({ date: DateOnlySchema.optional(), section: SectionFilterSchema });
export const SectionQuerySchema = z.object({ section: SectionFilterSchema });
/** The desk's follow-up list: the largest debts first, or the longest unpaid (no entry for longest). */
export const RECEIVABLES_SORTS = ['balance', 'oldest'] as const;
export type ReceivablesSort = (typeof RECEIVABLES_SORTS)[number];
export const ReceivablesQuerySchema = z.object({
  section: SectionFilterSchema,
  sort: z.enum(RECEIVABLES_SORTS).default('balance'),
});

export const DayPaymentsResponseSchema = z.object({
  date: DateOnlySchema,
  items: z.array(
    z.object({
      entryId: UuidSchema,
      type: z.enum(['payment', 'patient_refund', 'provider_payment']),
      section: z.enum(CLINIC_SECTIONS),
      patientId: UuidSchema,
      patientName: z.string(),
      amountRial: RialStringSchema,
      method: z.enum(PAYMENT_METHODS).nullable(),
      providerName: z.string().nullable(),
      reversed: z.boolean(),
      createdAt: IsoDateTimeSchema,
    }),
  ),
  /** Net of refunds and reversals, per method. */
  totalsByMethod: z.array(z.object({ method: z.enum(PAYMENT_METHODS), amountRial: RialStringSchema })),
  totalRial: RialStringSchema,
  providerTotals: z.array(
    z.object({
      providerId: UuidSchema,
      providerName: z.string(),
      amountRial: RialStringSchema,
    }),
  ),
  providerTotalRial: RialStringSchema,
});
export type DayPaymentsResponse = z.infer<typeof DayPaymentsResponseSchema>;

// ---- reports (BR-11: profit = revenue − material purchases − other expenses) ----

const MoneyTotalsSchema = z.object({
  revenueRial: RialStringSchema,
  chargesRial: RialStringSchema,
  discountsRial: RialStringSchema,
  adjustmentsRial: RialStringSchema,
  materialPurchasesRial: RialStringSchema,
  expensesRial: RialStringSchema,
  profitRial: RialStringSchema,
  paymentsRial: RialStringSchema,
  refundsRial: RialStringSchema,
  /** Paid to suppliers for purchases on credit (ADR-038). */
  vendorPaymentsRial: RialStringSchema,
  /** Cash received: cash payments + settlements from providers (ADR-058). */
  takingsRial: RialStringSchema,
  /** Gross provider payments received from patients (ADR-058). */
  providerPaymentsRial: RialStringSchema,
  /** Fees charged by payment providers (ADR-058). */
  providerFeesRial: RialStringSchema,
  /** Actual settlements received from payment providers (ADR-058). */
  providerSettlementsRial: RialStringSchema,
  /** Cash in minus cash out (payments − refunds − cash expenses and purchases − vendor payments + provider settlements). */
  netCashRial: RialStringSchema,
});

export const DayReportSchema = MoneyTotalsSchema.extend({
  date: DateOnlySchema,
  /** The section the report was narrowed to; null = every section. */
  section: z.enum(CLINIC_SECTIONS).nullable(),
  visitsCharged: z.number().int(),
  paymentsByMethod: z.array(z.object({ method: z.enum(PAYMENT_METHODS), amountRial: RialStringSchema })),
});
export type DayReport = z.infer<typeof DayReportSchema>;

/** A Jalali month: `year` 1405, `month` 7 = مهر. */
export const MonthQuerySchema = z.object({
  year: z.coerce.number().int().min(1390).max(1500),
  month: z.coerce.number().int().min(1).max(12),
  section: SectionFilterSchema,
});
export const MonthReportSchema = MoneyTotalsSchema.extend({
  section: z.enum(CLINIC_SECTIONS).nullable(),
  year: z.number().int(),
  month: z.number().int(),
  from: DateOnlySchema,
  to: DateOnlySchema,
  expensesByCategory: z.array(z.object({ category: z.string(), amountRial: RialStringSchema })),
  days: z.array(
    z.object({ date: DateOnlySchema, revenueRial: RialStringSchema, netCashRial: RialStringSchema }),
  ),
});
export type MonthReport = z.infer<typeof MonthReportSchema>;

export const RangeQuerySchema = z
  .object({ from: DateOnlySchema, to: DateOnlySchema, section: SectionFilterSchema })
  .refine((q) => q.from <= q.to, { message: 'بازه‌ی تاریخ معتبر نیست.', path: ['to'] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 400 * 86_400_000, {
    message: 'بازه حداکثر ۴۰۰ روز است.',
    path: ['to'],
  });

/** Service profit (BR-11): revenue minus the service's estimated material cost, for this report only. */
export const ServiceReportSchema = z.object({
  from: DateOnlySchema,
  to: DateOnlySchema,
  section: z.enum(CLINIC_SECTIONS).nullable(),
  items: z.array(
    z.object({
      serviceId: UuidSchema,
      serviceName: z.string(),
      charged: z.number().int(),
      packageSessions: z.number().int(),
      revenueRial: RialStringSchema,
      estMaterialCostRial: RialStringSchema,
      estProfitRial: RialStringSchema,
    }),
  ),
});
export type ServiceReport = z.infer<typeof ServiceReportSchema>;

export const PractitionerEarningsSchema = z.object({
  membershipId: UuidSchema,
  name: z.string(),
  charged: z.number().int(),
  packageSessions: z.number().int(),
  revenueRial: RialStringSchema,
  shareRial: RialStringSchema,
});
export type PractitionerEarnings = z.infer<typeof PractitionerEarningsSchema>;
export const PractitionerReportSchema = z.object({
  from: DateOnlySchema,
  to: DateOnlySchema,
  section: z.enum(CLINIC_SECTIONS).nullable(),
  items: z.array(PractitionerEarningsSchema),
});
export type PractitionerReport = z.infer<typeof PractitionerReportSchema>;

// ---- practitioner share (ADR-022) ----

export const ShareRuleSchema = z.object({
  id: UuidSchema,
  membershipId: UuidSchema,
  memberName: z.string(),
  serviceId: UuidSchema.nullable(),
  serviceName: z.string().nullable(),
  shareBp: z.number().int().nullable(),
  fixedRial: RialStringSchema.nullable(),
  effectiveFrom: IsoDateTimeSchema,
  /** False once a later rule for the same member and service took over. */
  current: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type ShareRule = z.infer<typeof ShareRuleSchema>;
export const ShareRuleListResponseSchema = z.object({ items: z.array(ShareRuleSchema) });

export const ShareRuleInputSchema = z
  .object({
    membershipId: UuidSchema,
    serviceId: UuidSchema.nullish().transform((v) => v ?? null),
    shareBp: z
      .number()
      .int()
      .min(0)
      .max(10_000)
      .nullish()
      .transform((v) => v ?? null),
    fixedRial: AmountRialSchema.nullish().transform((v) => v ?? null),
  })
  .refine((v) => (v.shareBp === null) !== (v.fixedRial === null), {
    message: 'یا درصد سهم را بدهید یا مبلغ ثابت را.',
    path: ['shareBp'],
  })
  .refine((v) => v.fixedRial === null || !isRialString(v.fixedRial) || BigInt(v.fixedRial) >= 0n, {
    message: 'مبلغ ثابت منفی نیست.',
    path: ['fixedRial'],
  });
export type ShareRuleInput = z.input<typeof ShareRuleInputSchema>;

// ---- packages (ADR-021) ----

export const SERVICE_PACKAGE_STATUSES = ['active', 'archived'] as const;
export const PACKAGE_ITEMS_MAX = 10;
export const PACKAGE_INSTALLMENTS_MAX = 24;
export const ServicePackageSchema = z.object({
  id: UuidSchema,
  /** The first service; `items` has them all. */
  serviceId: UuidSchema,
  serviceName: z.string(),
  /** The section of its services: who may sell it (ADR-036). */
  section: z.enum(CLINIC_SECTIONS),
  name: z.string(),
  /** All sessions, gifts included. */
  sessions: z.number().int(),
  items: z.array(
    z.object({
      serviceId: UuidSchema,
      serviceName: z.string(),
      sessions: z.number().int(),
      giftSessions: z.number().int(),
    }),
  ),
  /** The fixed price of a sale (whole toman). */
  priceRial: RialStringSchema,
  validDays: z.number().int().nullable(),
  deskDiscountMaxBp: z.number().int(),
  paymentTerms: z.enum(PACKAGE_PAYMENT_TERMS),
  minDownPaymentBp: z.number().int(),
  maxInstallments: z.number().int(),
  cancelPolicy: z.enum(CANCEL_POLICIES),
  cancelPenaltyBp: z.number().int(),
  maxExtensionDays: z.number().int(),
  status: z.enum(SERVICE_PACKAGE_STATUSES),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type ServicePackage = z.infer<typeof ServicePackageSchema>;
export const ServicePackageListResponseSchema = z.object({ items: z.array(ServicePackageSchema) });
export const ServicePackageListQuerySchema = z.object({
  includeArchived: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
});

/** Basis points (1% = 100). */
const BpSchema = z.number().int().min(0).max(10_000);
const PackageItemInputSchema = z.object({
  serviceId: UuidSchema,
  sessions: z.number().int().min(1, { message: 'هر خدمت دست‌کم یک جلسه دارد.' }).max(100),
  giftSessions: z.number().int().min(0).max(100).default(0),
});

/**
 * A package definition (ADR-037). The single-service shape of ADR-021 (`serviceId`, `sessions`)
 * is still accepted and becomes one row. The price is fixed (a service has no price; ADR-044).
 */
export const ServicePackageInputSchema = z
  .object({
    name: z.string().trim().min(1, { message: 'نام پکیج را بنویسید.' }).max(80),
    items: z.array(PackageItemInputSchema).min(1).max(PACKAGE_ITEMS_MAX).optional(),
    serviceId: UuidSchema.optional(),
    sessions: z.number().int().min(1).max(100).optional(),
    priceRial: PositiveRialSchema,
    validDays: z
      .number()
      .int()
      .min(1)
      .max(1095)
      .nullish()
      .transform((v) => v ?? null),
    deskDiscountMaxBp: BpSchema.default(0),
    paymentTerms: z.enum(PACKAGE_PAYMENT_TERMS).default('any'),
    minDownPaymentBp: BpSchema.default(0),
    maxInstallments: z.number().int().min(1).max(PACKAGE_INSTALLMENTS_MAX).default(1),
    cancelPolicy: z.enum(CANCEL_POLICIES).default('pro_rata'),
    cancelPenaltyBp: BpSchema.default(0),
    maxExtensionDays: z.number().int().min(0).max(365).default(0),
  })
  .transform(({ serviceId, sessions, items, ...rest }) => ({
    ...rest,
    items: items ?? (serviceId && sessions ? [{ serviceId, sessions, giftSessions: 0 }] : []),
  }))
  .superRefine((v, ctx) => {
    if (v.items.length === 0) {
      ctx.addIssue({ code: 'custom', message: 'دست‌کم یک خدمت برای پکیج انتخاب کنید.', path: ['items'] });
      return;
    }
    if (new Set(v.items.map((i) => i.serviceId)).size !== v.items.length) {
      ctx.addIssue({ code: 'custom', message: 'هر خدمت یک بار در پکیج می‌آید.', path: ['items'] });
    }
    const total = v.items.reduce((n, i) => n + i.sessions + i.giftSessions, 0);
    if (total < 2 || total > 100) {
      ctx.addIssue({ code: 'custom', message: 'پکیج بین ۲ تا ۱۰۰ جلسه دارد.', path: ['items'] });
    }
  });
export type ServicePackageInput = z.input<typeof ServicePackageInputSchema>;

export const PackageSaleInputSchema = z.object({
  servicePackageId: UuidSchema,
  /** A discount at the sale: up to the package's desk ceiling, or the whole price for the admin. */
  discountRial: PositiveRialSchema.nullish().transform((v) => v ?? null),
  /** Paid now, recorded with the sale (the same Idempotency-Key). */
  downPayment: z
    .object({
      amountRial: PositiveRialSchema,
      method: z
        .enum(PAYMENT_METHODS)
        .nullish()
        .transform((v) => v ?? null),
      provider: ProviderRefInputSchema.nullish().transform((v) => v ?? null),
    })
    .refine((d) => (d.method === null) !== (d.provider === null), {
      message: 'روش پرداخت یا سرویس پرداخت را انتخاب کنید.',
      path: ['method'],
    })
    .nullish()
    .transform((v) => v ?? null),
  installments: z
    .array(z.object({ dueOn: DateOnlySchema, amountRial: PositiveRialSchema }))
    .max(PACKAGE_INSTALLMENTS_MAX)
    .default([]),
});
export type PackageSaleInput = z.input<typeof PackageSaleInputSchema>;
export const PackageSaleResultSchema = z.object({
  package: PatientPackageSchema,
  entry: JournalEntrySchema,
  balance: PatientBalanceSchema,
});
export type PackageSaleResult = z.infer<typeof PackageSaleResultSchema>;

/** The payment plan of what a patient still owes after a visit (ADR-044). */
export const VisitPlanInputSchema = z.object({
  installments: z
    .array(z.object({ dueOn: DateOnlySchema, amountRial: PositiveRialSchema }))
    .min(1, { message: 'دست‌کم یک قسط بنویسید.' })
    .max(PACKAGE_INSTALLMENTS_MAX),
});
export type VisitPlanInput = z.input<typeof VisitPlanInputSchema>;
export const VisitPlanSchema = z.object({
  installments: z.array(z.object({ seq: z.number().int(), dueOn: DateOnlySchema, amountRial: RialStringSchema })),
});
export type VisitPlan = z.infer<typeof VisitPlanSchema>;

export const PackageCancelInputSchema = z.object({ reason: ReasonSchema });
export const PackageCancelResultSchema = z.object({
  package: PatientPackageSchema,
  /** The credit for the unused sessions; null when nothing was left. */
  entry: JournalEntrySchema.nullable(),
  balance: PatientBalanceSchema,
});
/** What cancelling now would return, by the package's own terms. */
export const PackageCancelPreviewSchema = z.object({
  cancelPolicy: z.enum(CANCEL_POLICIES),
  cancelPenaltyBp: z.number().int(),
  remainingSessions: z.number().int(),
  /** Remaining sessions that were paid for (gifts are lost first). */
  paidRemainingSessions: z.number().int(),
  creditRial: RialStringSchema,
});
export type PackageCancelPreview = z.infer<typeof PackageCancelPreviewSchema>;

export const PackageExtendInputSchema = z.object({ expiresOn: DateOnlySchema });

// ---- clinic finance settings (ADR-037) ----

export const FinanceSettingsSchema = z.object({
  /** A payment may exceed the debt: the patient keeps a credit for later services. */
  allowAdvancePayment: z.boolean(),
  updatedAt: IsoDateTimeSchema.nullable(),
});
export type FinanceSettings = z.infer<typeof FinanceSettingsSchema>;
export const FinanceSettingsInputSchema = z.object({ allowAdvancePayment: z.boolean() });
export type FinanceSettingsInput = z.infer<typeof FinanceSettingsInputSchema>;

// ---- the finance part of the three effects, and reversing a completed visit ----

export const FinanceEffectSchema = z.object({
  /** The visit's section: its balance is the one shown (ADR-036). */
  section: z.enum(CLINIC_SECTIONS),
  lines: z.array(
    z.object({
      visitItemId: UuidSchema,
      serviceName: z.string(),
      /** Charged now; "0" for a free service or a package session. */
      amountRial: RialStringSchema,
      packageName: z.string().nullable(),
      /** e.g. 3 = the third session of the package. */
      packageSession: z.number().int().nullable(),
      packageSessions: z.number().int().nullable(),
    }),
  ),
  totalRial: RialStringSchema,
  balanceBeforeRial: RialStringSchema,
  /** After the charges and the discount below. */
  balanceAfterRial: RialStringSchema,
  /** The discount given at completion ("0" when none), recorded as a `discount` entry of the visit. */
  discountRial: RialStringSchema,
  /** The largest discount allowed: the visit's charges, and never more than the patient then owes (BR-15). */
  maxDiscountRial: RialStringSchema,
});
export type FinanceEffect = z.infer<typeof FinanceEffectSchema>;

export const VisitReversePreviewSchema = z.object({
  section: z.enum(CLINIC_SECTIONS),
  charges: z.array(z.object({ entryId: UuidSchema, serviceName: z.string(), amountRial: RialStringSchema })),
  packageSessions: z.number().int(),
  /** Materials used in the visit stay used: stock does not come back. */
  materialsUsed: z.number().int(),
  balanceBeforeRial: RialStringSchema,
  balanceAfterRial: RialStringSchema,
});
export type VisitReversePreview = z.infer<typeof VisitReversePreviewSchema>;

// ---- nightly reconciliation (§Y 3.4), platform only; ids, never patient data ----

export const ReconciliationRunSchema = z.object({
  id: UuidSchema,
  ranAt: IsoDateTimeSchema,
  ok: z.boolean(),
  checks: z.array(
    z.object({ name: z.string(), violations: z.number().int(), sampleIds: z.array(UuidSchema) }),
  ),
});
export const ReconciliationListResponseSchema = z.object({ items: z.array(ReconciliationRunSchema) });
